import type {Post} from "~/repository/types";
import type {Ref} from "vue";
import {useToast} from "~/components/ui/toast";
import type Comment from '~/repository/models/Comment'

export const usePostAPI = (post: Ref<Post>) => {
    const {$api} = useNuxtApp()
    const {toast} = useToast()
    const newPostState = useState('newPost');

    async function like() {
        const {is_liked, likes_count} = await $api.posts.like(post.value)
        post.value.is_liked = post.value.likes_count < likes_count
        post.value.likes_count = likes_count
    }

    async function pin() {
        const {is_pinned} = await $api.posts.pin(post.value);
        post.value.is_pinned = is_pinned
        await nextTick(() => {
            //@todo Move to lang files
            toast({
                title: 'Przypięto u góry',
                duration: 1100
            });
        })
    }

    async function repost() {
        newPostState.value = await $api.posts.repost(post.value.source_post_id ?? post.value.id)
        await nextTick(() => {
            //@todo Move to lang files
            toast({
                title: 'Reposted',
                duration: 1100
            });
        });
    }

    async function unpin() {
        const {is_pinned} = await $api.posts.unpin(post.value);
        post.value.is_pinned = is_pinned
        await nextTick(() => {
            //@todo Move to lang files
            toast({
                title: 'Odpięto',
                duration: 1100
            });
        });
    }

    async function remove() {
        await $api.posts.delete(post.value);
        await nextTick(() => {
            //@todo Move to lang files
            toast({
                title: 'Post usunięty',
                duration: 1100
            });
        });
    }

    function useFetchComments() {
        const fetching = ref<Boolean | undefined>()
        const data = ref<{ [key: string]: Comment }>({})


        const addComment = (comment: Comment) => {
            if (comment.parent_id) {
                data.value[`comment_${comment.parent_id}`].children.push(comment)
            } else {
                data.value[`comment_${comment.id}`] = comment
            }
        }

        const removeComment = (commentId: number) => {
            const comment = data.value[`comment_${commentId}`];

            if (!comment) return;

            const deleteChildComments = (children: Comment[]) => {
                children.forEach(child => {
                    delete data.value[`comment_${child.id}`];
                    if (child.children.length > 0) {
                        deleteChildComments(child.children);
                    }
                });
            };

            if (comment.children.length > 0) {
                deleteChildComments(comment.children);
            }

            delete data.value[`comment_${commentId}`];

            Object.values(data.value).forEach(parentComment => {
                const initialLength = parentComment.children.length;
                parentComment.children = parentComment.children.filter(child => child.id !== commentId);
                
                if (initialLength > 0 && parentComment.children.length === 0) {
                    parentComment.children_count = 0;
                }
            });
        };


        const fetch = async () => await $api.posts.getComments(post.value.id, currentPage.value).then((paginatedData) => {
            data.value = {
                ...data.value,
                ...groupById(paginatedData.data, (id: number) => `comment_${id}`),
            };

            lastPage.value = paginatedData.meta.last_page;
        })

        const {currentPage, lastPage, loadMoreElement} = useInfinityScroll(fetch)


        watch(currentPage, async () => {
            fetching.value = true
            await fetch()
            fetching.value = false
        })

        onMounted(async () => {
            fetching.value = true
            await fetch();
            fetching.value = false
        })


        return {
            fetching,
            fetch,
            loadMoreElement,
            data,
            addComment,
            removeComment
        };
    }

    return {
        like,
        pin,
        unpin,
        remove,
        repost,

        //composable
        useFetchComments
    }
}